import { getCustomText, resolvePortalVars, sanitizeImageUrl } from "@/lib/portal-utils";
import type { PortalConfig } from "@/types/PortalConfig";
import type { PortalAiDisclosure } from "@/lib/api";

/**
 * Apply portal config to <head>: title, favicon, touch icon, og:* tags.
 * Called after the API response lands. Idempotent — safe to call again
 * on locale change.
 *
 * `variables` is the flat `{{variable}}` context (same as PortalClient) so
 * the title / og:* tags resolve placeholders just like the page body.
 *
 * `aiDisclosure` is the response's top-level field (Art. 50 Abs. 2 KI-VO):
 * one of the two WICG values sets `<meta name="ai-disclosure">`, anything
 * else (absent, an older worker, an unknown word) removes it. Applied BEFORE
 * the no-config early return — an event mark alone, with no stored portal
 * config, must still show.
 */
export function injectPortalHead(
  config: PortalConfig | undefined,
  eventName: string,
  locale: string,
  variables?: Record<string, unknown>,
  aiDisclosure?: PortalAiDisclosure | null,
): void {
  setNamedMeta("ai-disclosure", isAiDisclosure(aiDisclosure) ? aiDisclosure : null);

  if (!config) {
    document.title = eventName || "Portal";
    return;
  }

  // Merge the always-known eventName so a `{{eventName}}` title still resolves
  // even on an older backend that hasn't started sending `variables` yet.
  const ctx: Record<string, unknown> = { eventName, ...(variables ?? {}) };
  // `config.defaultLocale` is load-bearing: the edge shell
  // (portal-backend serve-portal-html) passes it, so leaving it out here made
  // the <title>/og:* a scraper sees differ from the one the booted SPA shows.
  const title = resolvePortalVars(
    getCustomText(config.pageTitleText, locale, eventName || "Portal", config.defaultLocale),
    ctx,
  );
  document.title = title;

  const favicon = sanitizeImageUrl(config.faviconUrl);
  if (favicon) upsertLink({ id: "favicon", rel: "icon", href: favicon });

  const touch = sanitizeImageUrl(config.touchIconUrl);
  if (touch) upsertLink({ rel: "apple-touch-icon", href: touch });

  const ogImage = sanitizeImageUrl(config.ogImageUrl);
  if (ogImage) upsertMeta({ property: "og:image", content: ogImage });

  const ogTitle = resolvePortalVars(
    getCustomText(config.ogTitle, locale, title, config.defaultLocale),
    ctx,
  );
  upsertMeta({ property: "og:title", content: ogTitle });

  const ogDesc = resolvePortalVars(
    getCustomText(config.ogDescription, locale, "", config.defaultLocale),
    ctx,
  );
  if (ogDesc) upsertMeta({ property: "og:description", content: ogDesc });

  if (config.customCss) injectCustomCss(config.customCss);
}

// Public-assets bucket CNAME. Company favicons are stored as permanent
// objects at `${PUBLIC_ASSETS_BASE}/company/{companyId}/favicon.png`
// (main-backend R2Service.buildPublicAssetUrl) — the same URL the cockpit
// persists into companies.portalFaviconUrl.
const PUBLIC_ASSETS_BASE = "https://assets.veltric.app";

/**
 * Tokenless company branding hint for the SHARED portal host
 * (portal.veltric.app), which — unlike a custom portal domain — can't map a
 * host to a company. If the link carries `?company=<id>` (or `?c=<id>`), swap
 * the favicon to that company's public favicon. The image is preloaded and
 * the swap only applies once it actually loads, so a company with no favicon
 * keeps the bundled Veltric default rather than showing a broken icon. A
 * later token load (with its event/company favicon) still overrides this, so
 * precedence stays event → company → default.
 */
export function applyCompanyFaviconHint(search: string): void {
  const params = new URLSearchParams(search);
  const id = (params.get("company") ?? params.get("c") ?? "").trim().toLowerCase();
  if (!/^[a-f0-9]{24}$/.test(id)) return;
  const href = `${PUBLIC_ASSETS_BASE}/company/${id}/favicon.png`;
  const img = new Image();
  img.onload = () => {
    upsertLink({ id: "favicon", rel: "icon", href });
    upsertLink({ rel: "apple-touch-icon", href });
  };
  img.src = href;
}

function upsertLink(opts: { id?: string; rel: string; href: string }) {
  let el: HTMLLinkElement | null = opts.id
    ? (document.getElementById(opts.id) as HTMLLinkElement | null)
    : document.querySelector<HTMLLinkElement>(`link[rel="${opts.rel}"]`);
  if (!el) {
    el = document.createElement("link");
    if (opts.id) el.id = opts.id;
    el.rel = opts.rel;
    document.head.appendChild(el);
  }
  el.href = opts.href;
}

function upsertMeta(opts: { property: string; content: string }) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${opts.property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", opts.property);
    document.head.appendChild(el);
  }
  el.content = opts.content;
}

/** The `name=` twin of {@link upsertMeta}; `content: null` removes the tag. */
function setNamedMeta(name: string, content: string | null) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (content === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.content = content;
}

/** Closed vocabulary: only these two literals ever reach the meta tag. */
function isAiDisclosure(v: unknown): v is PortalAiDisclosure {
  return v === "ai-generated" || v === "ai-assisted";
}

function injectCustomCss(css: string) {
  const id = "portal-custom-css";
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = css;
}
